import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import { 
  Gear,
  SignOut,
  Spinner,
  CheckCircle,
  XCircle,
  X,
  Download
} from "@phosphor-icons/react";

// Standardized localStorage keys
const STORAGE_KEYS = {
  LAST_OUTLET_ID: "ra:last_outlet_id",
};

// Debounce delay for auto code lookup (ms)
const CODE_LOOKUP_DEBOUNCE = 400;

export default function FastRedeem() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { canInstall, promptInstall } = useInstallPrompt();
  const urlOutletId = searchParams.get("outletId");
  
  // Outlet selection state
  const [outlets, setOutlets] = useState([]);
  const [selectedOutletId, setSelectedOutletId] = useState(null);
  
  // Form states
  const [code, setCode] = useState("");
  const [billAmount, setBillAmount] = useState("");
  
  // Data states
  const [outlet, setOutlet] = useState(null);
  const [reward, setReward] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState("");
  
  // Refs for auto-focus
  const codeInputRef = useRef(null);
  const billInputRef = useRef(null);

  // Fetch all outlets ONCE on mount
  useEffect(() => {
    const initializeOutlet = async () => {
      try {
        const { data } = await api.get("/outlets");
        const outletsData = data || [];
        
        setOutlets(outletsData);
        
        if (outletsData.length === 0) {
          setInitializing(false);
          return;
        }
        
        let targetOutletId = null;
        
        if (urlOutletId && outletsData.find(o => o.id === urlOutletId)) {
          targetOutletId = urlOutletId;
        } else {
          const storedId = localStorage.getItem(STORAGE_KEYS.LAST_OUTLET_ID);
          if (storedId && outletsData.find(o => o.id === storedId)) {
            targetOutletId = storedId;
          } else {
            targetOutletId = outletsData[0].id;
          }
        }
        
        setSelectedOutletId(targetOutletId);
        localStorage.setItem(STORAGE_KEYS.LAST_OUTLET_ID, targetOutletId);
        
        const targetOutlet = outletsData.find(o => o.id === targetOutletId);
        setOutlet(targetOutlet || null);
        
      } catch (e) {
        toast.error("Could not load outlet data");
      } finally {
        setInitializing(false);
        setTimeout(() => codeInputRef.current?.focus(), 100);
      }
    };
    
    initializeOutlet();
  }, [urlOutletId]);

  // Handle outlet switch
  const handleOutletSwitch = (e) => {
    const outletId = e.target.value;
    
    const newOutlet = outlets.find(o => o.id === outletId);
    
    if (!newOutlet) return;
    
    setSelectedOutletId(outletId);
    setOutlet(newOutlet);
    localStorage.setItem(STORAGE_KEYS.LAST_OUTLET_ID, outletId);
    
    resetForm();
    toast.success(`Switched to ${newOutlet.name}`);
  };

  // Reset form
  const resetForm = useCallback(() => {
    setCode("");
    setBillAmount("");
    setReward(null);
    setError("");
    setShowSuccess(false);
    setLoading(false);
    setChecking(false);
    setTimeout(() => codeInputRef.current?.focus(), 100);
  }, []);

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate("/login?redirect=%2Fapp%2Fredeem");
  };

  // Debounced code lookup
  useEffect(() => {
    if (code.length === 6) {
      fetchReward(code);
    } else if (code.length < 6) {
      setReward(null);
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Focus bill input when reward is ready
  useEffect(() => {
    if (reward && reward.status === "ready") {
      setTimeout(() => billInputRef.current?.focus(), 100);
    }
  }, [reward]);

  const fetchReward = async (rewardCode) => {
    setChecking(true);
    setError("");
    setReward(null);

    await new Promise((resolve) => setTimeout(resolve, CODE_LOOKUP_DEBOUNCE));

    try {
      const { data } = await api.get(`/redeem/lookup/${rewardCode}`);
      
      if (data.attempt?.outlet_id !== selectedOutletId) {
        setError("This code belongs to a different outlet");
        setReward({
          title: "Wrong Outlet",
          type: "ERROR",
          status: "invalid",
          value: 0,
        });
        setChecking(false);
        return;
      }
      
      if (data.attempt?.redeemed) {
        setError("This code has already been used");
        setReward({
          title: data.attempt.reward_label,
          type: "DISCOUNT",
          status: "redeemed",
          value: parseFloat(data.attempt.reward_value) || 0,
        });
        setChecking(false);
        return;
      }

      setReward({
        title: data.attempt.reward_label,
        type: data.attempt.reward_type === "discount" ? "DISCOUNT" : "FREE_ITEM",
        reward_type: data.attempt.reward_type, // Store actual reward_type from API
        status: "ready",
        value: parseFloat(data.attempt.reward_value) || 0,
        screenshot_url: data.attempt.screenshot_url || null,
      });
      
    } catch (e) {
      setError("Invalid code - please check and try again");
      setReward({
        title: data.attempt.reward_label,
        type: data.attempt.reward_type === "discount" ? "DISCOUNT" : "FREE_ITEM",
        reward_type: data.attempt.reward_type,
        status: "redeemed",
        value: parseFloat(data.attempt.reward_value) || 0,
        screenshot_url: data.attempt.screenshot_url || null,
      });
    } finally {
      setChecking(false);
    }
  };

  const handleCodeChange = (value) => {
    const uppercase = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (uppercase.length <= 6) {
      setCode(uppercase);
      if (uppercase.length < 6) {
        setReward(null);
        setError("");
      }
    }
  };

  const handleBillChange = (value) => {
    const numeric = value.replace(/[^0-9.]/g, "");
    setBillAmount(numeric);
  };

  const calculateDiscount = () => {
    if (!billAmount || !reward) return null;
    const bill = parseFloat(billAmount);
    if (isNaN(bill) || bill <= 0) return null;
    const discount = (bill * reward.value) / 100;
    const final = bill - discount;
    return { bill, discount, final };
  };

  const handleConfirmRedeem = async () => {
    if (!reward) return;

    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 200));

    try {
      // Only send bill details for discount rewards
      const body = {};
      if (reward.reward_type === "discount") {
        body.bill_amount = parseFloat(billAmount);
        body.discount_amount = calculateDiscount()?.discount;
        body.final_amount = calculateDiscount()?.final;
      }
      
      await api.post(`/redeem/${code}`, body);
      toast.success("Redeemed successfully!");
      resetForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to redeem");
      setLoading(false);
    }
  };

  const clearCode = () => {
    setCode("");
    setReward(null);
    setError("");
    codeInputRef.current?.focus();
  };

  const calculation = calculateDiscount();
  // For discount rewards: require calculation; for free items: just check ready status
  const canRedeem = reward?.status === "ready" && 
    (reward?.reward_type === "discount" ? calculation !== null : true);

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="animate-spin h-8 w-8 border-2 rounded-full" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }


  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--bg)' }}>
      {/* Compact Header */}
      <header className="px-4 py-3 flex items-center justify-between shrink-0" style={{ background: 'var(--surface)', borderBottom: '1.5px solid var(--ink)' }}>
        <div className="flex-1">
          <h1 className="font-black text-lg display" style={{ color: 'var(--ink)' }}>Quick Redeem</h1>
          {outlets.length > 0 && (
            <div className="relative mt-1 max-w-[200px]">
              <select
                value={selectedOutletId ?? ""}
                onChange={handleOutletSwitch}
                className="w-full appearance-none py-2 px-3 pr-8 rounded-xl text-sm font-bold cursor-pointer truncate"
                style={{ 
                  background: 'var(--secondary)', 
                  border: '1.5px solid var(--ink)',
                  color: 'var(--ink)'
                }}
              >
                {outlets.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2" style={{ color: 'var(--ink)' }}>
                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                </svg>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/app/settings")}
            className="w-9 h-9 flex items-center justify-center rounded-xl transition-colors"
            style={{ color: 'var(--ink)' }}
            aria-label="Settings"
          >
            <Gear size={22} weight="bold" />
          </button>
          <button
            onClick={handleLogout}
            className="w-9 h-9 flex items-center justify-center rounded-xl transition-colors"
            style={{ color: 'var(--ink)' }}
            aria-label="Logout"
          >
            <SignOut size={22} weight="bold" />
          </button>
        </div>
      </header>

      {/* Install Banner */}
      {canInstall && (
        <div className="mx-4 mt-3 p-3 rounded-xl flex items-center justify-between" style={{ background: 'var(--primary)', color: '#fff' }}>
          <div className="flex items-center gap-2">
            <Download size={18} weight="bold" />
            <span className="text-sm font-semibold">Install app for faster checkout ⚡</span>
          </div>
          <button
            onClick={promptInstall}
            className="px-3 py-1.5 rounded-lg text-sm font-bold"
            style={{ background: '#fff', color: 'var(--primary)' }}
          >
            Install
          </button>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col p-4 max-w-xl mx-auto w-full">
        <div className="flex-1 flex flex-col space-y-6 pt-4">
          {/* Code Input */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--ink-muted)' }}>
              Customer Code
            </label>
            <div className="relative">
              <input
                ref={codeInputRef}
                type="text"
                value={code}
                onChange={(e) => handleCodeChange(e.target.value)}
                placeholder="A4F2B1"
                inputMode="text"
                className="w-full h-16 px-4 pr-12 text-2xl font-black tracking-widest rounded-2xl uppercase outline-none transition-all"
                style={{ 
                  background: 'var(--surface)', 
                  border: '1.5px solid var(--ink)',
                  color: 'var(--ink)'
                }}
                maxLength={6}
              />
              {code.length > 0 && (
                <button
                  onClick={clearCode}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
                  style={{ color: 'var(--ink-muted)' }}
                  aria-label="Clear"
                >
                  <X size={20} weight="bold" />
                </button>
              )}
            </div>

            {checking && (
              <div className="mt-2 flex items-center gap-2 text-sm" style={{ color: 'var(--ink-muted)' }}>
                <Spinner className="w-4 h-4 animate-spin" />
                <span>Looking up...</span>
              </div>
            )}

            {error && (
              <div className="mt-2 flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--error)' }}>
                <XCircle className="w-4 h-4 flex-shrink-0" weight="bold" />
                <span>{error}</span>
              </div>
            )}
          </div>

          {/* Reward Info - Compact Inline */}
          {reward && reward.status === "ready" && (
            <div className="rounded-2xl px-4 py-4" style={{ background: 'var(--pastel-yellow)', border: '1.5px solid var(--ink)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-black display" style={{ color: 'var(--ink)' }}>{reward.title}</div>
                  <div className="text-sm font-semibold mt-1" style={{ color: 'var(--ink-muted)' }}>{reward.type} • Code: {code}</div>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 rounded-full font-bold text-xs" style={{ background: 'var(--success)', color: '#fff' }}>
                  <CheckCircle className="w-4 h-4" weight="bold" />
                  READY
                </div>
              </div>
              
              {/* Screenshot preview - show before redemption so owner can verify */}
              {reward.screenshot_url && (
                <div className="mt-4 pt-4 border-t" style={{ borderColor: 'var(--ink)' }}>
                  <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] mb-2">Customer's review screenshot</div>
                  <a 
                    href={reward.screenshot_url.startsWith("http") ? reward.screenshot_url : `${API.replace('/api', '')}${reward.screenshot_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-lg overflow-hidden border-2 border-[var(--ink)]"
                  >
                    <img 
                      src={reward.screenshot_url.startsWith("http") ? reward.screenshot_url : `${API.replace('/api', '')}${reward.screenshot_url}`}
                      alt="Customer review screenshot"
                      className="w-full max-h-48 object-contain bg-white"
                    />
                  </a>
                </div>
              )}
            </div>
          )}

          {reward && reward.status === "redeemed" && (
            <div className="rounded-2xl px-4 py-4" style={{ background: 'var(--secondary)', border: '1.5px solid var(--ink)' }}>
              <div className="flex items-center gap-3">
                <XCircle className="w-6 h-6 flex-shrink-0" weight="bold" style={{ color: 'var(--ink-muted)' }} />
                <div>
                  <div className="font-black display" style={{ color: 'var(--ink-muted)' }}>{reward.title}</div>
                  <div className="text-sm font-semibold" style={{ color: 'var(--ink-muted)' }}>Already used • Code: {code}</div>
                </div>
              </div>
            </div>
          )}

          {/* Bill Input + Calculation - Only for discount rewards */}
          {reward && reward.status === "ready" && reward.reward_type === "discount" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--ink-muted)' }}>
                  Bill Amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold" style={{ color: 'var(--ink-muted)' }}>
                    ₹
                  </span>
                  <input
                    ref={billInputRef}
                    type="text"
                    value={billAmount}
                    onChange={(e) => handleBillChange(e.target.value)}
                    placeholder="0"
                    inputMode="decimal"
                    className="w-full h-14 pl-10 pr-4 text-xl font-black rounded-2xl outline-none transition-all"
                    style={{ 
                      background: 'var(--surface)', 
                      border: '1.5px solid var(--ink)',
                      color: 'var(--ink)'
                    }}
                  />
                </div>
              </div>

              {calculation && (
                <div className="rounded-2xl p-4 space-y-3" style={{ background: 'var(--pastel-blue)', border: '1.5px solid var(--ink)' }}>
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold" style={{ color: 'var(--ink-muted)' }}>Bill</span>
                    <span className="font-black">₹{calculation.bill.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-bold" style={{ color: 'var(--success)' }}>
                    <span>You save ({reward.value}%)</span>
                    <span>- ₹{calculation.discount.toFixed(2)}</span>
                  </div>
                  <div className="pt-2 flex justify-between items-center border-t" style={{ borderColor: 'var(--ink)' }}>
                    <span className="text-sm font-bold" style={{ color: 'var(--ink-muted)' }}>Customer pays</span>
                    <span className="text-3xl font-black display" style={{ color: 'var(--primary)' }}>₹{calculation.final.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Confirm Button */}
        {canRedeem && (
          <div className="pt-4 pb-2 shrink-0">
            <button
              onClick={handleConfirmRedeem}
              disabled={loading}
              className="w-full h-14 rounded-2xl font-black text-lg transition-all display disabled:opacity-50"
              style={{ 
                background: 'var(--primary)', 
                color: '#fff',
                border: '1.5px solid var(--ink)',
                boxShadow: '3px 3px 0 0 var(--ink)'
              }}
            >
              {loading ? "Processing..." : "Confirm & Redeem"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}